import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import adminApi from '@/lib/adminApi';

interface AdminUser {
  id: string;
  email: string;
  name: string;
}

interface AdminAuthState {
  admin: AdminUser | null;
  isAuthenticated: boolean;
  accessToken: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  checkAuth: () => Promise<void>;
  setAccessToken: (accessToken: string) => void;
}

export const useAdminAuthStore = create<AdminAuthState>()(
  persist(
    (set, get) => ({
      admin: null,
      isAuthenticated: false,
      accessToken: null,

      setAccessToken: (accessToken: string) => set({ accessToken, isAuthenticated: true }),

      login: async (email, password) => {
        const response = await adminApi.post<{
          accessToken: string;
          refreshToken: string;
          expiresIn: number;
        }>('/auth/login', { email, password });

        // Set token first so the next request can attach it
        set({ accessToken: response.data.accessToken, isAuthenticated: true });

        const meResponse = await adminApi.get<AdminUser>('/auth/me');
        set({ admin: meResponse.data });
      },

      logout: () => {
        set({ admin: null, isAuthenticated: false, accessToken: null });
      },

      checkAuth: async () => {
        const token = get().accessToken;
        if (!token) {
          set({ isAuthenticated: false, admin: null });
          return;
        }
        try {
          const response = await adminApi.get<AdminUser>('/auth/me');
          set({ admin: response.data, isAuthenticated: true });
        } catch {
          set({ admin: null, isAuthenticated: false, accessToken: null });
        }
      },
    }),
    {
      name: 'admin-auth',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        admin: state.admin,
        isAuthenticated: state.isAuthenticated,
        accessToken: state.accessToken,
      }),
    },
  ),
);
